import { useParams } from 'react-router-dom';
import { Note } from 'models/ComunicationHub';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CommunicationHubEntityType } from 'components/common/communicationHub/index';
import { INoteContent, useInfinityNoteListInfiniteQuery, usePinnedNoteListQuery } from 'api';

const tabOptions = [
  { label: 'All', value: '' },
  { label: 'Clients', value: 'CLIENT' },
  {
    label: 'Suppliers',
    value: 'SUPPLIER',
  },
];

const mergePages = (pages: INoteContent[]): { notes: Record<string, Note[]>; pinnedNotes: Record<string, Note[]> } => {
  return pages.reduce(
    (acc, page) => {
      page.content?.forEach(({ notes, pinnedNotes }) => {
        for (const [date, arr] of Object.entries(pinnedNotes)) {
          if (!acc.pinnedNotes[date]) acc.pinnedNotes[date] = [];
          acc.pinnedNotes[date].push(...arr);
        }
        for (const [date, arr] of Object.entries(notes)) {
          if (!acc.notes[date]) acc.notes[date] = [];
          acc.notes[date].push(...arr);
        }
      });
      return acc;
    },
    {
      notes: {} as Record<string, Note[]>,
      pinnedNotes: {} as Record<string, Note[]>,
    }
  );
};

export const useCommunicationHub = ({
  entity,
  entityId,
}: {
  entity: CommunicationHubEntityType;
  entityId?: string;
}) => {
  const [addModalState, setAddModalState] = useState(false);
  const [activeTab, setActiveTab] = React.useState<string>('');
  const { id } = useParams() as { id: string };
  const observerLastRef = useRef<IntersectionObserver | null>(null);
  const observerFirstRef = useRef<IntersectionObserver | null>(null);
  const {
    data: pinnedNotes,
    isLoading: pinnedListLoading,
    isFetching: pinnedListFetching,
  } = usePinnedNoteListQuery({ entityId: entityId || id, entityName: entity, filterBy: activeTab });
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    isLoading: noteListLoading,
  } = useInfinityNoteListInfiniteQuery({ size: 15, entityId: entityId || id, entityName: entity, filterBy: activeTab });
  const onAddModalStateChange = useCallback((value: boolean) => setAddModalState(value), []);

  const notes = useMemo(() => {
    const { notes } = mergePages(data?.pages || []);
    return notes;
  }, [data]);

  const onActiveTabChange = useCallback((key: string) => {
    setActiveTab(key);
  }, []);

  const lastNoteRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isFetchingNextPage) return;
      if (observerLastRef.current) observerLastRef.current.disconnect();
      observerLastRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );
      observerLastRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );
  const firstNoteRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isFetchingPreviousPage) return;
      if (observerFirstRef.current) observerFirstRef.current.disconnect();
      observerFirstRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasPreviousPage && !isFetchingPreviousPage) {
            fetchPreviousPage();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );
      observerFirstRef.current.observe(node);
    },
    [isFetchingPreviousPage, hasPreviousPage, fetchPreviousPage]
  );
  return {
    addModalState,
    onAddModalStateChange,
    notes: notes || {},
    pinnedNotes: pinnedNotes || {},
    isFetchingNextPage,
    isFetchingPreviousPage,
    lastNoteRef,
    firstNoteRef,
    isLoading: pinnedListLoading || pinnedListFetching || noteListLoading,
    tabOptions,
    activeTab,
    onActiveTabChange,
  };
};
